import axios from "axios";
import { Text } from "lucide-react";
import { useState } from "react";
import { Button } from "src/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "src/components/ui/dialog";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/components/ui/select";

interface ManualInvoiceFormProps {
  kostenstellen: string[];
  paymentReasons: string[];
  financingItems: string[];
  sellers: string[];
  onSuccess?: () => void;
}

const initialForm = {
  invoiceNumber: "",
  invoiceDate: "",
  sellerName: "",
  sellerPostalCode: "",
  sellerCity: "",
  sellerStreet: "",
  buyerName: "",
  buyerPostalCode: "",
  buyerCity: "",
  buyerStreet: "",
  grossAmount: "",
  netAmount: "",
  currencyCode: "EUR",
  dueDate: "",
  paymentReference: "",
  paymentMethod: "",
  iban: "",
  bic: "",
  vatAmount: "",
  vatRate: "",
  orderDate: "",
  paymentDate: "",
  paymentReason: "",
  financingPlanItem: "",
  kostenstelle: "",
};

export const ManualInvoiceForm: React.FC<ManualInvoiceFormProps> = ({
  kostenstellen,
  paymentReasons,
  financingItems,
  sellers,
  onSuccess,
}) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialForm);

  // Für alle Freitextfelder (neuen Verkäufer etc.)
  const [manualSeller, setManualSeller] = useState("");
  const [manualKostenstelle, setManualKostenstelle] = useState("");
  const [manualReason, setManualReason] = useState("");
  const [manualItem, setManualItem] = useState("");

  // Validation + Pflichtfelder
  const requiredFields = [
    "invoiceNumber",
    "invoiceDate",
    "sellerName",
    "grossAmount",
    "netAmount",
    "kostenstelle",
    "paymentReason",
    "financingPlanItem",
    "orderDate",
  ];

  const isValid = requiredFields.every((key) =>
    key === "sellerName"
      ? manualSeller || form.sellerName
      : key === "kostenstelle"
      ? manualKostenstelle || form.kostenstelle
      : key === "paymentReason"
      ? manualReason || form.paymentReason
      : key === "financingPlanItem"
      ? manualItem || form.financingPlanItem
      : (form as any)[key]
  );

  const handleSubmit = async () => {
    const token = localStorage.getItem("authToken");
    // Payload analog zu deinem Interface
    const payload = {
      ...form,
      sellerName: manualSeller || form.sellerName,
      kostenstelle: manualKostenstelle || form.kostenstelle,
      paymentReason: manualReason || form.paymentReason,
      financingPlanItem: manualItem || form.financingPlanItem,
      grossAmount: form.grossAmount ? parseFloat(form.grossAmount) : null,
      netAmount: form.netAmount ? parseFloat(form.netAmount) : null,
      vatAmount: form.vatAmount ? parseFloat(form.vatAmount) : null,
      vatRate: form.vatRate ? parseFloat(form.vatRate) : null,
      invoiceDate: form.invoiceDate || null,
      dueDate: form.dueDate || null,
      orderDate: form.orderDate || null,
      paymentDate: form.paymentDate || null,
    };

    try {
      await axios.post("/api/accounting/invoices", payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOpen(false);
      setForm(initialForm);
      setManualSeller("");
      setManualKostenstelle("");
      setManualReason("");
      setManualItem("");
      onSuccess?.();
    } catch (err: any) {
      alert(err.response?.data?.message || "Fehler beim Speichern.");
      console.error(err);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-1/2 h-full text-xl flex items-center justify-center gap-6">
          <Text className="w-10 h-10" />
          Rechnung manuell erfassen
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manuelle Rechnungserfassung</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          {/* Rechnungsdaten */}
          <div>
            <Label>Rechnungsnummer *</Label>
            <Input value={form.invoiceNumber} onChange={(e) => setForm({ ...form, invoiceNumber: e.target.value })} />
          </div>
          <div>
            <Label>Rechnungsdatum *</Label>
            <Input
              type="date"
              value={form.invoiceDate}
              onChange={(e) => setForm({ ...form, invoiceDate: e.target.value })}
            />
          </div>

          {/* Verkäuferdaten */}
          <div>
            <Label>Verkäufer *</Label>
            <Select value={form.sellerName} onValueChange={(value) => setForm({ ...form, sellerName: value })}>
              <SelectTrigger>
                <SelectValue placeholder="Wählen..." />
              </SelectTrigger>
              <SelectContent>
                {sellers.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              className="mt-2"
              placeholder="Oder neuen Verkäufer eingeben"
              value={manualSeller}
              onChange={(e) => setManualSeller(e.target.value)}
            />
            <div className="flex gap-2 mt-2">
              <Input
                placeholder="Straße"
                value={form.sellerStreet}
                onChange={(e) => setForm({ ...form, sellerStreet: e.target.value })}
              />
              <Input
                placeholder="PLZ"
                value={form.sellerPostalCode}
                onChange={(e) => setForm({ ...form, sellerPostalCode: e.target.value })}
              />
              <Input
                placeholder="Ort"
                value={form.sellerCity}
                onChange={(e) => setForm({ ...form, sellerCity: e.target.value })}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">Freitext überschreibt Auswahl</p>
          </div>

          {/* Käuferdaten */}
          <div>
            <Label>Käufer</Label>
            <Input
              placeholder="Name"
              value={form.buyerName}
              onChange={(e) => setForm({ ...form, buyerName: e.target.value })}
            />
            <div className="flex gap-2 mt-2">
              <Input
                placeholder="Straße"
                value={form.buyerStreet}
                onChange={(e) => setForm({ ...form, buyerStreet: e.target.value })}
              />
              <Input
                placeholder="PLZ"
                value={form.buyerPostalCode}
                onChange={(e) => setForm({ ...form, buyerPostalCode: e.target.value })}
              />
              <Input
                placeholder="Ort"
                value={form.buyerCity}
                onChange={(e) => setForm({ ...form, buyerCity: e.target.value })}
              />
            </div>
          </div>

          {/* Beträge */}
          <div className="flex gap-4">
            <div className="flex-1">
              <Label>Bruttobetrag *</Label>
              <Input
                type="number"
                step="0.01"
                value={form.grossAmount}
                onChange={(e) => setForm({ ...form, grossAmount: e.target.value })}
              />
            </div>
            <div className="flex-1">
              <Label>Nettobetrag *</Label>
              <Input
                type="number"
                step="0.01"
                value={form.netAmount}
                onChange={(e) => setForm({ ...form, netAmount: e.target.value })}
              />
            </div>
          </div>
          <div className="flex gap-4">
            <div className="flex-1">
              <Label>USt.-Betrag</Label>
              <Input
                type="number"
                step="0.01"
                value={form.vatAmount}
                onChange={(e) => setForm({ ...form, vatAmount: e.target.value })}
              />
            </div>
            <div className="flex-1">
              <Label>USt.-Satz (%)</Label>
              <Input
                type="number"
                step="0.01"
                value={form.vatRate}
                onChange={(e) => setForm({ ...form, vatRate: e.target.value })}
              />
            </div>
            <div className="flex-1">
              <Label>Währung</Label>
              <Input value={form.currencyCode} onChange={(e) => setForm({ ...form, currencyCode: e.target.value })} />
            </div>
          </div>

          {/* Bankdaten */}
          <div className="flex gap-4">
            <div className="flex-1">
              <Label>IBAN</Label>
              <Input value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} />
            </div>
            <div className="flex-1">
              <Label>BIC</Label>
              <Input value={form.bic} onChange={(e) => setForm({ ...form, bic: e.target.value })} />
            </div>
          </div>
          <div className="flex gap-4">
            <div className="flex-1">
              <Label>Zahlungsmethode</Label>
              <Input value={form.paymentMethod} onChange={(e) => setForm({ ...form, paymentMethod: e.target.value })} />
            </div>
            <div className="flex-1">
              <Label>Zahlungsreferenz</Label>
              <Input
                value={form.paymentReference}
                onChange={(e) => setForm({ ...form, paymentReference: e.target.value })}
              />
            </div>
          </div>

          {/* Datumsangaben */}
          <div className="flex gap-4">
            <div className="flex-1">
              <Label>Fälligkeitsdatum</Label>
              <Input type="date" value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} />
            </div>
            <div className="flex-1">
              <Label>Bestelldatum *</Label>
              <Input
                type="date"
                value={form.orderDate}
                onChange={(e) => setForm({ ...form, orderDate: e.target.value })}
              />
            </div>
            <div className="flex-1">
              <Label>Zahlungsdatum</Label>
              <Input
                type="date"
                value={form.paymentDate}
                onChange={(e) => setForm({ ...form, paymentDate: e.target.value })}
              />
            </div>
          </div>

          {/* Kostenstelle */}
          <div>
            <Label>Kostenstelle *</Label>
            <Select value={form.kostenstelle} onValueChange={(value) => setForm({ ...form, kostenstelle: value })}>
              <SelectTrigger>
                <SelectValue placeholder="Wählen..." />
              </SelectTrigger>
              <SelectContent>
                {kostenstellen.map((k) => (
                  <SelectItem key={k} value={k}>
                    {k}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              className="mt-2"
              placeholder="Oder neue Kostenstelle eingeben"
              value={manualKostenstelle}
              onChange={(e) => setManualKostenstelle(e.target.value)}
            />
            <p className="text-xs text-muted-foreground mt-1">Freitext überschreibt Auswahl</p>
          </div>

          {/* Zahlungsgrund */}
          <div>
            <Label>Zahlungsgrund *</Label>
            <Select value={form.paymentReason} onValueChange={(value) => setForm({ ...form, paymentReason: value })}>
              <SelectTrigger>
                <SelectValue placeholder="Wählen..." />
              </SelectTrigger>
              <SelectContent>
                {paymentReasons.map((r) => (
                  <SelectItem key={r} value={r}>
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              className="mt-2"
              placeholder="Oder neuen Zahlungsgrund eingeben"
              value={manualReason}
              onChange={(e) => setManualReason(e.target.value)}
            />
            <p className="text-xs text-muted-foreground mt-1">Freitext überschreibt Auswahl</p>
          </div>

          {/* Finanzierungsposition */}
          <div>
            <Label>Finanzierungsposition *</Label>
            <Select
              value={form.financingPlanItem}
              onValueChange={(value) => setForm({ ...form, financingPlanItem: value })}>
              <SelectTrigger>
                <SelectValue placeholder="Wählen..." />
              </SelectTrigger>
              <SelectContent>
                {financingItems.map((i) => (
                  <SelectItem key={i} value={i}>
                    {i}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              className="mt-2"
              placeholder="Oder neue Finanzierungsposition eingeben"
              value={manualItem}
              onChange={(e) => setManualItem(e.target.value)}
            />
            <p className="text-xs text-muted-foreground mt-1">Freitext überschreibt Auswahl</p>
          </div>

          <Button className="mt-2" onClick={handleSubmit} disabled={!isValid}>
            Speichern
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
